import { LANGUAGE_EN } from '@home/_config/config';
import Abnormal from '@home/components/Abnormal';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { Col, Row, SearchBox } from 'tea-component';
import { useIntl } from 'umi';
import AgentHomeSearchIcon from '../svg/agent-home-search.svg';
import SkillCard from './components/SkillCard';
import CategoryTabs from './components/CategoryTabs';
import SkillsListEmpty from './components/SkillsListEmpty';
import { useSkillsData } from './hooks';
import { ALL_CATEGORY } from './types';
import './index.scss';

/**
 * Skills 集合页面组件
 */
const SkillsCollectionPage = () => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;

  // 搜索关键词
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  // 当前激活的分类TAB
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY);

  // 数据来源
  const {
    categoryTabs,
    loading,
    handleInstallSkill,
    handleDownloadSkill,
    getFilteredSkills,
    isSkillDownloading,
  } = useSkillsData(isEN);

  // 过滤后的 Skills 列表（分类 + 搜索双重过滤）
  const filteredSkills = useMemo(() => {
    return getFilteredSkills(activeCategory, searchKeyword, isEN);
  }, [activeCategory, searchKeyword, isEN, getFilteredSkills]);

  // 处理搜索变化
  const handleSearchChange = (value: string) => {
    setSearchKeyword(value);
  };

  // 处理分类TAB切换
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
  };

  // 是否显示分类 TAB（超过 2 个才显示）
  const showCategoryTabs = categoryTabs.length > 2;

  const searchField = (
    <div
      className={classNames('dba-skills-collection__search', {
        'dba-skills-collection__search--toolbar': showCategoryTabs,
      })}
    >
      <img src={AgentHomeSearchIcon} alt="" className="dba-skills-collection__search-icon" />
      <SearchBox
        className="dba-skills-search-box"
        placeholder={formatMessage({ id: 'dba_skills_collection_search_placeholder' })}
        onChange={handleSearchChange}
      />
    </div>
  );

  return (
    <div
      className={classNames('dba-skills-collection', {
        'dba-skills-collection--with-category-tabs': showCategoryTabs,
      })}
    >
      <div className="dba-skills-collection__header">
        <h1 className="dba-skills-collection__title">
          {formatMessage({ id: 'dba_skills_collection_title' })}
        </h1>
        {!showCategoryTabs && searchField}
      </div>

      {showCategoryTabs && (
        <div className="dba-skills-collection__toolbar">
          <div className="dba-skills-collection__tabs-wrap">
            <CategoryTabs
              activeCategory={activeCategory}
              onChange={handleCategoryChange}
              tabs={categoryTabs}
            />
          </div>
          {searchField}
        </div>
      )}

      <Abnormal
        isLoading={loading}
        isNoData={false}
        isError={false}
        component={
          <>
            {/* Skills 列表 */}
            <div className="dba-skills-collection__list">
              <Row>
                {filteredSkills?.map((skill) => (
                  <Col key={skill.skill_id} span={8}>
                    <SkillCard
                      skill={skill}
                      searchKeyword={searchKeyword}
                      onInstall={(skillId) => void handleInstallSkill(skillId)}
                      onDownload={(s) => void handleDownloadSkill(s)}
                      isDownloading={isSkillDownloading(skill.skill_id)}
                    />
                  </Col>
                ))}
              </Row>

              {/* 空状态 */}
              {filteredSkills.length === 0 && <SkillsListEmpty />}
            </div>
          </>
        }
      />
    </div>
  );
};

export default SkillsCollectionPage;
